import {
	useMutation,
	useQuery,
	type UseMutationResult,
	type UseQueryOptions,
	type UseQueryResult,
} from '@tanstack/react-query';
import { SERVER_ROUTES } from '../../../shared/constants/routes.constants';
import type {
	CreateCategoryRequestSchemaType,
	UpdateCategoryRequestSchemaType,
} from '../../../shared/schemas/categorySchemas/categoryRequests.schema';
import { generatePath } from '../../../shared/services/app.shared-service';
import type { CategoryEntity } from '../../../shared/types/category.entity';
import type { ErrorResponse } from '../../../shared/types/error';
import type { DataWithFile } from '../types/form';
import { objectToFormData } from '../utils/api.utils';
import {
	type CategoryListResponseSchemaType,
	type CategoryResponseSchemaType,
} from './../../../shared/schemas/categorySchemas/categoryResponses.schema';
import { axiosClient, queryClient } from './api';

const CATEGORY_QUERY_KEY = 'categories';
const baseUrl = [SERVER_ROUTES.CATEGORY.version, SERVER_ROUTES.CATEGORY.BASE];

function upsertCategoriesListCache(
	queryKey: unknown[],
	updatedCategory: CategoryEntity
) {
	queryClient.setQueryData<CategoryListResponseSchemaType['categories']>(
		queryKey,
		(oldList) => {
			if (!oldList) return oldList;

			let isNewCategory = true;
			const newList = oldList.map((item) => {
				if (item.id === updatedCategory.id) {
					isNewCategory = false;
					return updatedCategory;
				}
				return item;
			});
			if (isNewCategory) {
				return [...newList, updatedCategory];
			}
			return newList;
		}
	);
}

function removeCategoryFromListCache(
	id: CategoryEntity['id'],
	queryKey: unknown[]
) {
	queryClient.setQueryData<CategoryListResponseSchemaType['categories']>(
		queryKey,
		(oldList) => {
			if (!oldList) return oldList;

			return oldList.filter((item) => item.id !== id);
		}
	);
}

function removeCategoryFromCache(
	id: CategoryEntity['id'],
	queryKey?: unknown[]
) {
	queryClient.setQueryData<CategoryResponseSchemaType['category']>(
		queryKey ?? [CATEGORY_QUERY_KEY, id],
		undefined
	);
}

function updateCategoryCache(
	updatedCategory: CategoryEntity,
	queryKey?: unknown[]
) {
	queryClient.setQueryData<CategoryResponseSchemaType['category']>(
		queryKey ?? [CATEGORY_QUERY_KEY, updatedCategory.id],
		updatedCategory
	);
}

async function fetchCategoriesList(): Promise<
	CategoryListResponseSchemaType['categories']
> {
	const response = await axiosClient.get<CategoryListResponseSchemaType>(
		generatePath({
			route: [...baseUrl, SERVER_ROUTES.CATEGORY.ROUTES.LIST],
		})
	);
	return response.data.categories;
}

/**
 * Custom hook to fetch categories list.
 */
export function useCategoriesList(
	options?: UseQueryOptions<
		CategoryListResponseSchemaType['categories'],
		ErrorResponse
	>
): UseQueryResult<CategoryListResponseSchemaType['categories'], ErrorResponse> {
	return useQuery<
		CategoryListResponseSchemaType['categories'],
		ErrorResponse
	>({
		queryKey: [CATEGORY_QUERY_KEY, SERVER_ROUTES.CATEGORY.ROUTES.LIST],
		queryFn: async () => {
			const listData = await fetchCategoriesList();

			// Seed the cache for each category
			for (const category of listData) {
				updateCategoryCache(category);
			}

			return listData;
		},
		...options,
	});
}

async function fetchCategoryById(
	id: string
): Promise<CategoryResponseSchemaType['category']> {
	const response = await axiosClient.get<CategoryResponseSchemaType>(
		generatePath({
			route: [...baseUrl, SERVER_ROUTES.CATEGORY.ROUTES.GET_BY_ID],
			params: { id },
		})
	);
	return response.data.category;
}

/**
 * Custom hook to fetch a category by its ID.
 */
export function useCategoryById(
	id: string | undefined,
	options?: Partial<
		UseQueryOptions<CategoryResponseSchemaType['category'], ErrorResponse>
	>
): UseQueryResult<CategoryResponseSchemaType['category'], ErrorResponse> {
	return useQuery<CategoryResponseSchemaType['category'], ErrorResponse>({
		queryKey: [CATEGORY_QUERY_KEY, id],
		enabled: !!id,
		queryFn: async () => {
			const category = await fetchCategoryById(id!);
			updateCategoryCache(category);
			upsertCategoriesListCache(
				[CATEGORY_QUERY_KEY, SERVER_ROUTES.CATEGORY.ROUTES.LIST],
				category
			);
			return category;
		},
		...options,
	});
}

async function createCategory(
	data: DataWithFile<CreateCategoryRequestSchemaType, 'image'>
): Promise<CategoryResponseSchemaType['category']> {
	const formData = objectToFormData(data);
	const response = await axiosClient.post<CategoryResponseSchemaType>(
		generatePath({
			route: [...baseUrl, SERVER_ROUTES.CATEGORY.ROUTES.CREATE],
		}),
		formData,
		{
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}
	);
	return response.data.category;
}

/**
 * Custom hook to create a new category.
 */
export function useCreateCategory(): UseMutationResult<
	CategoryResponseSchemaType['category'],
	ErrorResponse,
	DataWithFile<CreateCategoryRequestSchemaType, 'image'>
> {
	return useMutation({
		retry: 0,
		mutationFn: (data) => createCategory(data),
		onSuccess: (data) => {
			updateCategoryCache(data);
			upsertCategoriesListCache(
				[CATEGORY_QUERY_KEY, SERVER_ROUTES.CATEGORY.ROUTES.LIST],
				data
			);
			return data;
		},
	});
}

async function updateCategory(
	id: CategoryEntity['id'],
	data: DataWithFile<UpdateCategoryRequestSchemaType, 'image'>
): Promise<CategoryResponseSchemaType['category']> {
	const formData = objectToFormData(data);
	const response = await axiosClient.put<CategoryResponseSchemaType>(
		generatePath({
			route: [...baseUrl, SERVER_ROUTES.CATEGORY.ROUTES.UPDATE],
			params: { id },
		}),
		formData,
		{
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}
	);
	return response.data.category;
}

/**
 * Custom hook to update an existing category.
 */
export function useUpdateCategory(): UseMutationResult<
	CategoryResponseSchemaType['category'],
	ErrorResponse,
	{
		id: CategoryEntity['id'];
		data: DataWithFile<UpdateCategoryRequestSchemaType, 'image'>;
	}
> {
	return useMutation({
		retry: 0,
		mutationFn: ({ id, data }) => updateCategory(id, data),
		onSuccess: (data, variables) => {
			updateCategoryCache(data, [CATEGORY_QUERY_KEY, variables.id]);
			upsertCategoriesListCache(
				[CATEGORY_QUERY_KEY, SERVER_ROUTES.CATEGORY.ROUTES.LIST],
				data
			);
			return data;
		},
	});
}

async function deleteCategory(id: CategoryEntity['id']): Promise<boolean> {
	const response = await axiosClient.delete<{ success: boolean }>(
		generatePath({
			route: [...baseUrl, SERVER_ROUTES.CATEGORY.ROUTES.DELETE],
			params: { id },
		})
	);
	return response.data.success;
}

/**
 * Custom hook to delete a category by its ID.
 */
export function useDeleteCategory(): UseMutationResult<
	boolean,
	ErrorResponse,
	CategoryEntity['id']
> {
	return useMutation({
		retry: 0,
		mutationFn: (id) => deleteCategory(id),
		onSuccess: (data, id) => {
			if (!data) {
				return data;
			}
			removeCategoryFromCache(id);
			removeCategoryFromListCache(id, [
				CATEGORY_QUERY_KEY,
				SERVER_ROUTES.CATEGORY.ROUTES.LIST,
			]);
			return data;
		},
	});
}
